import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { X, Bell } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '../contexts/AuthContext';
import { requestNativeNotificationPermission } from '../services/nativePush';

const API = '/api';
const STORAGE_KEY = 'lastSeenVictoryId';
const SHOW_DELAY_MS = 10_000;

// Routes where showing the popup would be disruptive (login, modals, etc.)
const EXCLUDED_ROUTES = ['/login', '/verify-email', '/auth/google/callback', '/privacy-policy', '/cookie-policy', '/terms'];

const LatestVictoryPopup = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [victory, setVictory] = useState(null);
  const [visible, setVisible] = useState(false);
  // iter101 (strategy 4): contextual push-opt-in nudge shown INSIDE the
  // victory popup when the user has notifications off. The moment they've
  // just seen a real cassa is the highest-emotional-engagement window we
  // have — perfect timing for a soft ask. We hide it entirely for admins,
  // for users who already have push enabled, and for guests (no user).
  const [pushHint, setPushHint] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const shownRef = useRef(false);  // Only show once per app session
  const confettiTimeoutsRef = useRef([]);

  // Fetch the latest victory once the user is logged in and on a non-excluded route
  useEffect(() => {
    if (!user || shownRef.current) return;
    if (EXCLUDED_ROUTES.some(r => location.pathname.startsWith(r))) return;
    // Admins don't need to see their own announcements
    if (user.is_admin) return;

    // Debug/preview helper: append ?force_victory_popup=1 to bypass the
    // one-shot localStorage flag. Useful for stakeholder review.
    const forcePopup = new URLSearchParams(location.search).get('force_victory_popup') === '1';

    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/victory-gallery`);
        const items = Array.isArray(res.data) ? res.data : [];
        if (cancelled || items.length === 0) return;
        const latest = items[0];
        if (!forcePopup) {
          const lastSeen = localStorage.getItem(STORAGE_KEY);
          if (lastSeen === latest.image_id) return;  // User already saw this one
        }
        setVictory(latest);
      } catch (e) {
        // Silent fail — popup is non-critical
      }
    })();
    return () => { cancelled = true; };
  }, [user, location.pathname, location.search]);

  // Show the popup after the navigation delay
  useEffect(() => {
    if (!victory || shownRef.current) return;
    const timer = setTimeout(() => {
      shownRef.current = true;
      setVisible(true);
    }, SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [victory]);

  // iter101 (strategy 4): once the popup becomes visible, quickly check
  // whether push is off and stash the hint state. We call the same
  // /auth/push-softask endpoint used elsewhere — it classifies the OS
  // state (never asked / dismissed / granted / denied).
  useEffect(() => {
    if (!visible || !user || user.is_admin) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/push-softask`);
        if (cancelled) return;
        const s = (r.data || {}).status;
        // Only nudge users who could still turn it on with a single click.
        // 'system_denied' users are handled by the ReengagementBanner+tutorial.
        if (s === 'not_asked' || s === 'shown' || s === 'dismissed') {
          setPushHint(true);
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [visible, user]);

  // Confetti bursts while visible
  useEffect(() => {
    if (!visible) return;
    const fire = () => {
      confetti({
        particleCount: 80,
        spread: 90,
        startVelocity: 45,
        origin: { x: Math.random(), y: 0.2 },
        colors: ['#F5A623', '#FF6B6B', '#FFD700', '#ffffff'],
        zIndex: 9999,
      });
    };
    fire();
    const t1 = setTimeout(fire, 400);
    const t2 = setTimeout(fire, 900);
    const t3 = setTimeout(fire, 1500);
    confettiTimeoutsRef.current = [t1, t2, t3];
    return () => {
      confettiTimeoutsRef.current.forEach(clearTimeout);
      confetti.reset?.();
    };
  }, [visible]);

  const close = () => {
    if (victory) {
      try { localStorage.setItem(STORAGE_KEY, victory.image_id); } catch {}
    }
    setVisible(false);
  };

  const join = (prefilledMessage) => {
    close();
    navigate('/chat', { state: { prefilledMessage } });
  };

  // iter101 (strategy 4): fire the native OS prompt from the emotional peak
  const handleEnablePush = async () => {
    if (pushBusy) return;
    setPushBusy(true);
    try {
      await axios.post(`${API}/auth/push-softask`, { action: 'accepted' }).catch(() => {});
      const token = await requestNativeNotificationPermission();
      const granted = !!token;
      await axios.post(`${API}/auth/push-softask`, {
        action: granted ? 'system_granted' : 'system_denied',
      }).catch(() => {});
      if (granted) {
        try {
          await axios.post(`${API}/notifications/register-token`, {
            token,
            platform: Capacitor.getPlatform(),
          });
        } catch { /* re-registers on next boot */ }
      }
      setPushHint(false);
    } finally {
      setPushBusy(false);
    }
  };

  if (!visible || !victory) return null;

  // ───────── CTA variant based on user tier ─────────
  //   Gold       → no button, only close (they already have everything)
  //   VIP-non-Gold → upgrade CTA to the 7 tipster community
  //   Non-VIP    → access-VIP CTA
  let ctaCfg;
  if (user?.is_gold) {
    ctaCfg = { show: false, subtitle: 'Hai visto questa analisi?' };
  } else if (user?.is_vip) {
    ctaCfg = {
      show: true,
      subtitle: "Unisciti alla community dei 7 tipster!",
      label: 'Richiedi accesso ai 7 tipster',
      message: 'Ciao voglio accedere alla community dei 7 tipster mi dai info?',
      testid: 'latest-victory-join-tipster-btn',
    };
  } else {
    ctaCfg = {
      show: true,
      subtitle: "Unisciti all'area vip/gold!",
      label: 'Unisciti',
      message: 'Voglio accedere alle analisi VIP',
      testid: 'latest-victory-join-btn',
    };
  }

  return (
    <div
      className="fixed inset-0 z-[9000] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-[fadeIn_0.3s_ease-out]"
      data-testid="latest-victory-popup"
      onClick={close}
    >
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes popIn { 0% { opacity: 0; transform: scale(0.6) rotate(-5deg) } 60% { opacity: 1; transform: scale(1.06) rotate(2deg) } 100% { transform: scale(1) rotate(0deg) } }
        @keyframes pulseGold { 0%, 100% { text-shadow: 0 0 24px rgba(245,166,35,0.6), 0 4px 0 rgba(0,0,0,0.4) } 50% { text-shadow: 0 0 48px rgba(245,166,35,1), 0 4px 0 rgba(0,0,0,0.4) } }
      `}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-sm bg-gradient-to-br from-[#1A1A1A] via-[#242424] to-[#0F0F0F] rounded-3xl border-2 border-[#F5A623]/40 shadow-2xl shadow-[#F5A623]/30 overflow-hidden"
        style={{ animation: 'popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards' }}
      >
        {/* Close button */}
        <button
          onClick={close}
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center text-white transition-colors"
          data-testid="latest-victory-close-btn"
          aria-label="Chiudi"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="px-6 pt-7 pb-2 text-center">
          <h1
            className="font-black text-[2.6rem] leading-[0.95] tracking-tight text-transparent bg-clip-text bg-gradient-to-b from-[#FFD86B] via-[#F5A623] to-[#FF6B6B]"
            style={{ fontFamily: 'Barlow Condensed, Impact, sans-serif', animation: 'pulseGold 1.6s ease-in-out infinite' }}
          >
            BOOOM!!
          </h1>
          <p className="text-white text-sm font-bold mt-3">Analisi andata a segno!!! Te l'eri persa?</p>
          <p className="text-[#F5A623] text-sm font-extrabold uppercase tracking-wider mt-0.5">{ctaCfg.subtitle}</p>
        </div>

        {/* Image — contain-fit so the whole screenshot is always visible
            regardless of aspect ratio (9:16, 4:3, 1:1, etc.). */}
        <div className="px-5 pt-3 pb-4">
          <div className="rounded-2xl overflow-hidden bg-black border border-white/10 flex items-center justify-center" style={{ maxHeight: '55vh' }}>
            <img
              src={victory.image_url}
              alt="Vittoria"
              className="w-full h-auto max-h-[55vh] object-contain"
              data-testid="latest-victory-img"
            />
          </div>
        </div>

        {/* iter101 strategy 4: contextual push opt-in. Only appears for
            non-admin users whose OS state is still "asks allowed". */}
        {pushHint && (
          <div className="px-5 pb-3">
            <button
              onClick={handleEnablePush}
              disabled={pushBusy}
              data-testid="latest-victory-push-optin"
              className="w-full flex items-center gap-3 px-3.5 py-3 rounded-xl bg-gradient-to-r from-[#F5A623]/15 to-[#FF6B6B]/15 border border-[#F5A623]/40 hover:border-[#F5A623]/70 active:scale-[0.99] transition-all disabled:opacity-60"
            >
              <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#F5A623]/20 border border-[#F5A623]/40 flex items-center justify-center">
                <Bell className="w-4 h-4 text-[#F5A623]" />
              </div>
              <div className="flex-1 min-w-0 text-left">
                <div className="text-[13px] font-black text-white leading-tight uppercase tracking-wide">
                  Non farti scappare la prossima
                </div>
                <div className="text-[11px] text-gray-400 leading-tight mt-0.5">
                  Attiva le notifiche e sei il primo a saperlo
                </div>
              </div>
              <span className="text-[11px] font-black text-[#F5A623] uppercase tracking-wider flex-shrink-0">
                {pushBusy ? '…' : 'ATTIVA'}
              </span>
            </button>
          </div>
        )}

        {/* CTA — hidden for Gold users (they already have access to everything) */}
        {ctaCfg.show && (
          <div className="px-5 pb-6">
            <button
              onClick={() => join(ctaCfg.message)}
              className="w-full py-3.5 rounded-xl bg-gradient-to-r from-[#F5A623] via-[#FF6B6B] to-[#F5A623] text-[#1A1A1A] font-black uppercase tracking-[0.2em] text-sm shadow-lg shadow-[#F5A623]/40 hover:brightness-110 active:scale-[0.98] transition-all"
              data-testid={ctaCfg.testid}
            >
              {ctaCfg.label}
            </button>
          </div>
        )}
        {/* Gold users get a little bottom padding so the modal still feels balanced */}
        {!ctaCfg.show && <div className="pb-4" />}
      </div>
    </div>
  );
};

export default LatestVictoryPopup;
